import { forwardRef } from "react";
import {
  NavIconGiftees,
  NavIconHome,
  NavIconOccasions,
  NavIconSettings,
} from "./NavItemIcons.jsx";
import styles from "./NavItem.module.css";

/**
 * @typedef {'home' | 'giftees' | 'occasions' | 'settings'} NavItemDestination
 * Maps to Figma `Item`: Home, Giftees, Occasions, Settings
 */

const DEFAULT_LABELS = {
  home: "Home",
  giftees: "Giftees",
  occasions: "Occasions",
  settings: "Settings",
};

const ICONS = {
  home: NavIconHome,
  giftees: NavIconGiftees,
  occasions: NavIconOccasions,
  settings: NavIconSettings,
};

/**
 * Icon-only nav slot from Figma "Nav Items" (node 346:4525).
 * Selected: brand primary (#66f2c0) pill behind midnight stroke icon.
 */
const NavItem = forwardRef(function NavItem(
  {
    item,
    selected = false,
    icon: IconOverride,
    "aria-label": ariaLabelProp,
    className,
    disabled = false,
    type = "button",
    ...rest
  },
  ref,
) {
  const ResolvedIcon = IconOverride ?? ICONS[item];
  const ariaLabel =
    ariaLabelProp ?? DEFAULT_LABELS[item] ?? (item != null ? String(item) : "Navigation");

  const rootClass = [
    styles.root,
    selected && styles.selected,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconNode =
    ResolvedIcon == null
      ? null
      : typeof ResolvedIcon === "function"
        ? <ResolvedIcon />
        : ResolvedIcon;

  return (
    <button
      ref={ref}
      type={type}
      className={rootClass}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-current={selected ? "page" : undefined}
      {...rest}
    >
      <span className={styles.icon}>{iconNode}</span>
    </button>
  );
});

export default NavItem;
